import mixpanel from 'mixpanel-browser'
import { flags, type FeatureDefinition } from '../../openfeature/flags'
import { HostBrowserApis } from '../../utils/host-browser-apis'
import debug from '../../utils/console'
import { OpenFeature } from '@openfeature/web-sdk'
import { useOpenFeature } from '../useOpenFeature.browser'
let isMixpanelSDKInitiated = false

const flagKeys = Object.keys(flags)
type FeaturePrimitive = boolean | string | number
function mixpanelLog(name: string, eventData: Record<string, unknown> = {}) {
  window.mixpanelEventCallLog = window.mixpanelEventCallLog || []
  window.mixpanelEventCallLog.push({ name, eventData })
}

const log = debug.withTag('mixpanel')

/**
 * Performs some rudimentary filtering to ensure we do not re-fire the same event multiple times.
 * For example:
 *  - The auto-layout function can re-rendered the page during re-size which can cause multiple `NtpAd_Rendered` events to fire for direct ads.
 *  - PageViewed can be unintentionally sent twice if Layouts are nested. This will catch that problem as well.
 */
const sentAdRenderedPlacements: string[] = []
let sentPageViewedEvent: boolean = false
function shouldSendMixpanelEvent(name: string, eventData: unknown): boolean {
  /**
   * @fixme [DXT-953] This function is a boolean function that checks if the event is an NtpAdEvent.
   * It checks if the name is 'NtpAd_Rendered' and if the data is an object.
   * It should not have any side effects.
   * It should not modify any external state or variables.
   * It should not rely on any external state or variables.
   */
  const isNtpAdEvent = (name: string, data: unknown): data is NtpAdEvent =>
    Boolean(name === 'NtpAd_Rendered' && data && typeof data === 'object')

  if (isNtpAdEvent(name, eventData)) {
    if (eventData.ntp_ad_provider !== 'direct' || !eventData.ntp_placement_name)
      return true
    if (sentAdRenderedPlacements.includes(eventData.ntp_placement_name)) {
      // If the event was sent for this placement; do not resend
      return false
    }
    // Record that an event is sent of this placement
    sentAdRenderedPlacements.push(eventData.ntp_placement_name)
  }
  if (name === 'NtpPage_Viewed') {
    if (sentPageViewedEvent) return false
    sentPageViewedEvent = true
  }
  return true
}

export function resetPageViewedDeduplication(): void {
  sentPageViewedEvent = false
}

const contextAndFlagData = new Map<string, unknown>()
async function extractFlagAndContextData() {
  if (contextAndFlagData.has('metadata')) {
    return contextAndFlagData.get('metadata')
  }
  const { client } = await useOpenFeature()
  const getValue = (key: string, defaultValue: FeaturePrimitive) => {
    if (typeof defaultValue === 'string') {
      return client.getStringValue(key, defaultValue)
    } else if (typeof defaultValue === 'boolean') {
      return client.getBooleanValue(key, defaultValue)
    } else if (typeof defaultValue === 'number') {
      return client.getNumberValue(key, defaultValue)
    }
    throw new Error(`Unsupported default value type for key: ${key}`)
  }
  const results = await Promise.all(
    flagKeys.map(async (key) => {
      try {
        const flag: FeatureDefinition<FeaturePrimitive> = flags[key]
        return {
          [key]: await getValue(key, flag.defaultValue),
        }
      } catch (error) {
        log.warn(`Failed retrieving feature flag '${key}':`, error)
        return {}
      }
    })
  )
  const openFeature = results.reduce((acc, result) => {
    return Object.assign(acc, result)
  }, {})

  const metadata = {
    ...openFeature,
    ...OpenFeature.getContext(),
    // eslint-disable-next-line
    ...((client as any).apiContextAccessor?.() ?? {}),
    splittestname: client.getStringValue('splittestname', 'control'),
  }
  contextAndFlagData.set('metadata', metadata)
  return metadata
}
/**
 * Set up Mixpanel SDK
 * @description
 * This function initializes the Mixpanel SDK and registers feature flags with Mixpanel.
 * It checks if the Mixpanel token is available in the runtime configuration and initializes the SDK with it.
 * It also retrieves feature flags using OpenFeature and registers them with Mixpanel.
 * The function ensures that the Mixpanel SDK is only initialized once to avoid redundant initializations.
 * @returns
 */
async function initializeMixpanelSDK() {
  if (isMixpanelSDKInitiated) return mixpanel

  const config = useRuntimeConfig()
  if (!config.public.mixpanelToken) {
    throw new Error('Mixpanel token is not defined in the runtime config.')
  }
  mixpanel.init(config.public.mixpanelToken, {
    api_host: config.public.mixpanelApiHost,
  })
  log.info('Mixpanel SDK initialized')
  const metadata = await extractFlagAndContextData()
  log.info('Mixpanel metadata:', metadata)
  mixpanel.register(metadata)

  // Set cohort as Mixpanel People profile property for reporting
  const cohortStr = metadata?.cohort ? String(metadata.cohort) : ''
  const targetinKeyStr = metadata?.targetingKey
    ? String(metadata.targetingKey)
    : ''
  if (cohortStr && targetinKeyStr) {
    mixpanel.identify(metadata.targetingKey)
    if (mixpanel.people.set_once) {
      mixpanel.people.set_once({ cohort: cohortStr })
    } else {
      mixpanel.people?.set({ cohort: cohortStr })
    }
  }

  isMixpanelSDKInitiated = true
  return mixpanel
}

function sendEventWithMixpanelSDK(
  name: string,
  data?: Record<string, unknown>
) {
  if (!import.meta.client) return

  return initializeMixpanelSDK()
    .then(() => {
      mixpanel.track(name, data)
    })
    .catch(() => {
      log.error(
        'Failed to send event to Mixpanel. The Mixpanel SDK is not initialized.'
      )
    })
}

export async function sendEvent(
  name: string,
  eventData?: Record<string, unknown>
): Promise<void> {
  log.info(`Sending Mixpanel event: ${name}`, eventData)
  if (!import.meta.client) return

  if (!shouldSendMixpanelEvent(name, eventData)) return
  const metadata = await extractFlagAndContextData()

  const data = {
    ...eventData,
    split_test_data: metadata,
  }

  mixpanelLog(name, data)

  const { shift, chrome, onelaunch } = HostBrowserApis.getHostApis()

  const shiftAPI = shift?.analytics?.track
  if (shiftAPI) {
    shiftAPI(name, data)
    return
  }

  const oneLaunchAPI = onelaunch?.sendEvent
  if (oneLaunchAPI) {
    oneLaunchAPI(name, data)
    return
  }

  const chromeAPI = chrome?.runtime?.sendEvent
  if (chromeAPI) {
    chromeAPI(name, data)
    return
  }
  try {
    await sendEventWithMixpanelSDK(name, eventData)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    /**empty */
  }
}
